// Browser-local autosave for the multi-row tax encoding grids (VAT output/input,
// Percentage Tax sales/income). Pure storage layer — no React, no network.
//
// This is deliberately localStorage and deliberately NOT a server-side draft:
// a partially-typed VAT line is not a financial record, and the "posted is
// immutable" rule means no half-built row may ever reach VATRecord /
// PercentageTax. What we protect against is a closed tab, a crashed session, or
// a failed submit losing an hour of typing.
//
// Nothing auth- or tenant-switching-related is ever written here (per CLAUDE.md):
// clientId and userId appear in the KEY only, to scope a draft to one person
// working one client, never as credentials to be read back and trusted.

const KEY_PREFIX = 'vh.taxdraft.';

// Bump when the draft payload shape changes in a way an older draft can't
// satisfy. A mismatched version is discarded, never migrated and never blindly
// restored into a grid that no longer has those fields.
export const DRAFT_SCHEMA_VERSION = 1;

// Drafts older than this are swept on mount. Long enough to survive a weekend
// gap for someone who encodes on Friday afternoon; short enough that stale
// half-typed periods don't pile up in a shared browser profile.
export const MAX_DRAFT_AGE_MS = 48 * 60 * 60 * 1000;

export const DRAFT_SCREENS = {
  VAT_OUTPUT: 'vat-output',
  VAT_INPUT: 'vat-input',
  PT_SALES: 'pt-sales',
  PT_INCOME: 'pt-income',
  WHT_EWT: 'wht-ewt',
  WHT_COMP: 'wht-comp',
};

/**
 * Storage access is wrapped everywhere: localStorage throws outright in a
 * private window on some browsers, and setItem throws on quota. Losing autosave
 * is acceptable; breaking the encoding screen because of it is not.
 */
const readRaw = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeRaw = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const removeRaw = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing to do — the draft is already unreachable */
  }
};

/**
 * Deterministic stringify — object keys sorted recursively so two payloads with
 * the same content in a different key order hash identically. Array order is
 * preserved (row order is meaningful). Mirrors the backend's stableStringify in
 * middlewares/idempotency.js on purpose: both sides are hashing the same
 * submitted body, and they must agree on what "the same request" means.
 */
const stableStringify = (value) => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
};

/**
 * FNV-1a. Not a security hash — it only has to detect "the user edited this
 * group before retrying", which decides whether the Idempotency-Key may be
 * replayed or must be rotated.
 */
const hashPayload = (payload) => {
  const str = stableStringify(payload);
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

/**
 * Draft key. clientId + userId + screen + fiscalYear, so a draft can never be
 * restored into another tenant's client, another user's session on a shared
 * terminal, the sibling tab (output vs input), or a different filing year.
 * Any missing part yields null — no key, no autosave, rather than a draft
 * written under a partial identity that a later, fuller identity won't match.
 */
export const buildDraftKey = ({ clientId, userId, screen, fiscalYear }) => {
  if (!clientId || !userId || !screen || !fiscalYear) return null;
  return `${KEY_PREFIX}v${DRAFT_SCHEMA_VERSION}.${clientId}.${userId}.${screen}.${fiscalYear}`;
};

/**
 * Reads and validates a draft. Returns null — and deletes the entry — for
 * anything we can't vouch for: unparseable JSON, a version we don't speak, rows
 * that aren't an array, an expired draft, or a payload whose self-described
 * identity disagrees with the key it was found under (a hand-edited or
 * collided entry).
 */
export const loadDraft = (key, identity = null) => {
  if (!key) return null;

  const raw = readRaw(key);
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    removeRaw(key);
    return null;
  }

  const invalid =
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.v !== DRAFT_SCHEMA_VERSION ||
    !Array.isArray(parsed.rows) ||
    typeof parsed.savedAt !== 'number' ||
    Date.now() - parsed.savedAt > MAX_DRAFT_AGE_MS;

  if (invalid) {
    removeRaw(key);
    return null;
  }

  if (identity) {
    const mismatched =
      String(parsed.clientId) !== String(identity.clientId) ||
      String(parsed.userId) !== String(identity.userId) ||
      parsed.screen !== identity.screen ||
      String(parsed.fiscalYear) !== String(identity.fiscalYear);

    if (mismatched) {
      removeRaw(key);
      return null;
    }
  }

  return parsed;
};

/**
 * Writes rows verbatim.
 *
 * Money is NOT touched here — no Number(), no parseFloat, no toFixed. Whatever
 * the row state held is what round-trips, so a restored draft matches what the
 * user left rather than a reformatted version of it. (MoneyInput already
 * converts a keystroke to a number in row state; this layer adds no further
 * transformation of its own.)
 */
export const saveDraft = (key, { screen, clientId, userId, fiscalYear, rows, submitKeys }) => {
  if (!key) return null;

  const payload = {
    v: DRAFT_SCHEMA_VERSION,
    screen,
    clientId,
    userId,
    fiscalYear,
    savedAt: Date.now(),
    rows,
    submitKeys: submitKeys || {},
  };

  let serialized;
  try {
    serialized = JSON.stringify(payload);
  } catch {
    // A row holding something non-serializable (a DOM node from a picker, say)
    // — drop the write rather than corrupt the stored draft.
    return null;
  }

  return writeRaw(key, serialized) ? payload : null;
};

export const clearDraft = (key) => {
  if (!key) return;
  removeRaw(key);
};

/**
 * Rewrites the draft keeping only the rows NOT in confirmedRowIds — the rows
 * the server actually confirmed are the only ones safe to forget. Failed and
 * unknown-outcome rows stay, so they survive a refresh and can be retried
 * without re-typing. Deletes the entry when nothing is left to keep.
 *
 * `submitKeys` is passed in rather than taken from the stored draft: the keys
 * issued during the submit that just failed live in memory and may not have
 * been flushed to storage yet (the write is debounced). Reading them back from
 * storage here would discard the key the retry has to replay.
 */
export const clearConfirmedRowsFromDraft = (key, confirmedRowIds, identity, submitKeys) => {
  const draft = loadDraft(key, identity);
  if (!draft) return null;

  const confirmed = new Set((confirmedRowIds || []).map(String));
  const remaining = draft.rows.filter((row) => !confirmed.has(String(row?.id)));

  if (remaining.length === 0) {
    clearDraft(key);
    return null;
  }

  return saveDraft(key, {
    ...draft,
    rows: remaining,
    submitKeys: submitKeys ?? draft.submitKeys,
  });
};

/**
 * Sweeps every draft this app owns, dropping expired ones and ones written by a
 * schema version we no longer speak. Runs once per encoding-screen mount, so
 * stale drafts can't accumulate in a shared browser profile even for clients
 * and periods nobody reopens.
 */
export const purgeStaleDrafts = (now = Date.now()) => {
  let keys;
  try {
    keys = Object.keys(localStorage).filter((k) => k.startsWith(KEY_PREFIX));
  } catch {
    return 0;
  }

  let purged = 0;
  keys.forEach((key) => {
    const raw = readRaw(key);
    if (!raw) return;

    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      removeRaw(key);
      purged += 1;
      return;
    }

    const expired =
      !parsed ||
      parsed.v !== DRAFT_SCHEMA_VERSION ||
      typeof parsed.savedAt !== 'number' ||
      now - parsed.savedAt > MAX_DRAFT_AGE_MS;

    if (expired) {
      removeRaw(key);
      purged += 1;
    }
  });

  return purged;
};

/**
 * A stable Idempotency-Key per submit group (one VAT batch, or one quarter of a
 * Percentage Tax save), persisted alongside the draft.
 *
 * Retrying the SAME payload replays the same key, so the backend idempotency
 * middleware returns the cached response instead of inserting a second copy —
 * which is what makes "retry the rows whose outcome is unknown" safe at all.
 * Editing a failed row before retrying changes the hash and mints a fresh key:
 * replaying the old key with a different body is rejected 409
 * IDEMPOTENCY_KEY_REUSE by middlewares/idempotency.js, so rotating is required,
 * not optional.
 *
 * `stamp` is minted with the key and reused for as long as the key lives. The
 * VAT payload carries a transactionDate that would otherwise be `new Date()` on
 * every attempt — a retry would then send a different body under the same key
 * and be rejected as key reuse, defeating the whole mechanism. Callers that
 * need a timestamp in the body must use this one and must keep it out of the
 * hashed payload.
 *
 * Returns { key, stamp, submitKeys } — the caller persists submitKeys with the
 * draft.
 */
export const keyForGroup = (submitKeys, groupId, payload) => {
  const hash = hashPayload(payload);
  const existing = submitKeys?.[groupId];

  if (existing && existing.hash === hash && existing.key && existing.stamp) {
    return { key: existing.key, stamp: existing.stamp, submitKeys: submitKeys || {} };
  }

  const key = generateKey();
  const stamp = new Date().toISOString();
  return {
    key,
    stamp,
    submitKeys: { ...(submitKeys || {}), [groupId]: { key, hash, stamp } },
  };
};

/**
 * Drops a group's stored key once it is confirmed saved, so the next submit of
 * the same quarter is a genuinely new request rather than a replay of the one
 * that already succeeded.
 */
export const releaseGroupKey = (submitKeys, groupId) => {
  if (!submitKeys || !(groupId in submitKeys)) return submitKeys || {};
  const next = { ...submitKeys };
  delete next[groupId];
  return next;
};

const generateKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

// Exported for tests — the hashing contract (same payload → same key) is the
// thing that makes retry safe, so it is worth asserting directly.
export const __testables = { stableStringify, hashPayload, KEY_PREFIX };
